import type { ProjectDetail } from "@esi/shared";
import { FileText, Folder } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/EmptyState";
import { formatDate } from "../i18n/format.ts";
import type { WikiFolderIndex as Index } from "../projects/knowledge-tree.ts";
import { Link, wikiPath } from "../router.tsx";

/** `?folder=` on the Wiki's Pages view: a folder of the tree, by ID. */
export const wikiFolderPath = (projectId: string, folderId: string) =>
  `${wikiPath(projectId)}?folder=${encodeURIComponent(folderId)}`;

/**
 * A Wiki folder chosen with no page open (spec `project-wiki` "Folder index"): its subfolders,
 * then the readable pages directly in it with their type and last update, in the tree's order.
 * Built only from the reader-filtered index and the folder list, so it never counts or hints at
 * a page the viewer cannot read.
 */
export function WikiFolderIndex(props: { project: ProjectDetail; index: Index }) {
  const { project, index } = props;
  const { t } = useTranslation();
  const titleId = useId();
  const parents = index.path.slice(0, -1);
  const empty = index.subfolders.length === 0 && index.pages.length === 0;
  return (
    <section data-esi-content="wiki" aria-labelledby={titleId} className="flex flex-col gap-8">
      <header className="flex flex-col gap-1">
        <h2
          id={titleId}
          className="text-2xl font-semibold tracking-tight text-balance break-words text-fg"
        >
          {index.folder.name}
        </h2>
        <p className="flex flex-wrap items-center gap-x-1.5 text-sm text-fg-secondary">
          {parents.length ? (
            <>
              <span className="inline-flex min-w-0 items-center gap-1">
                <Folder aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
                <span className="break-words">{parents.join(" › ")}</span>
              </span>
              <span aria-hidden="true">·</span>
            </>
          ) : null}
          <span className="tabular-nums">
            {t("wiki.overview.pages", { count: index.pages.length })}
          </span>
        </p>
      </header>
      {empty ? (
        <EmptyState icon={Folder} headingLevel={3} title={t("wiki.folderIndex.empty.title")}>
          {t("wiki.folderIndex.empty.body")}
        </EmptyState>
      ) : (
        <ul
          aria-label={t("wiki.folderIndex.contents", { folder: index.folder.name })}
          className="divide-y divide-line rounded-lg border border-line"
        >
          {index.subfolders.map((folder) => (
            <li key={folder.id} className="px-4 py-3">
              <Link
                href={wikiFolderPath(project.id, folder.id)}
                className="inline-flex min-w-0 items-center gap-2 text-sm font-medium text-fg underline-offset-4 hover:underline"
              >
                <Folder
                  aria-hidden="true"
                  className="size-4 shrink-0 text-fg-muted"
                  strokeWidth={1.75}
                />
                <span className="break-words">{folder.name}</span>
              </Link>
            </li>
          ))}
          {index.pages.map((page) => (
            <li key={page.id} className="flex flex-col gap-1 px-4 py-3">
              <Link
                href={wikiPath(project.id, page.id)}
                className="inline-flex min-w-0 items-center gap-2 text-sm font-medium text-fg underline-offset-4 hover:underline"
              >
                <FileText
                  aria-hidden="true"
                  className="size-4 shrink-0 text-fg-muted"
                  strokeWidth={1.75}
                />
                <span className="break-words">{page.title}</span>
              </Link>
              <p className="flex flex-wrap items-center gap-x-1.5 pl-6 text-xs text-fg-muted">
                <span>{t(`wiki.pageTypes.${page.pageType}`)}</span>
                <span aria-hidden="true">·</span>
                <span>{t("wiki.overview.updated", { date: formatDate(page.updatedAt) })}</span>
                {page.status === "ready" ? null : (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{t(`wiki.overview.status.${page.status}`)}</span>
                  </>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
